import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCents, type Class, type MessageThread } from '@pirouette/shared';
import { Badge } from '@/components/ui/Badge';
import { supabase } from '@/lib/supabase';
import { formatTime12 } from '@/lib/time';

export const Route = createFileRoute('/parent/')({
  component: ParentToday,
});

interface ParentTodaySummary {
  classes: Class[];
  balances: AccountBalance[];
  latestThread: MessageThread | null;
}

interface AccountBalance {
  account_id: string;
  family_id: string;
  kind: 'studio' | 'competition' | 'custom';
  name: string;
  active: boolean;
  outstanding_cents: number;
  open_count: number;
  overdue_count: number;
}

function classTime(c: Class): string {
  return `${c.weekday} ${formatTime12(c.start_time)}–${formatTime12(c.end_time)}`;
}

function uniqueClasses(rows: { classes: Class | null }[]): Class[] {
  const seen = new Set<string>();
  return rows
    .map((r) => r.classes)
    .filter((c): c is Class => c !== null && c.deleted_at === null)
    .filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
}

function useParentToday() {
  return useQuery({
    queryKey: ['parent-today'],
    queryFn: async (): Promise<ParentTodaySummary> => {
      const userResponse = await supabase.auth.getUser();
      const familyId = userResponse.data.user?.app_metadata.family_id as string | undefined;

      const classesQuery = supabase
        .from('enrollments')
        .select('classes(*)')
        .eq('status', 'enrolled')
        .is('deleted_at', null);

      const balancesQuery = familyId
        ? supabase
            .from('account_balance')
            .select('*')
            .eq('family_id', familyId)
            .eq('active', true)
            .order('kind')
        : Promise.resolve({ data: [], error: null });

      const threadsQuery = supabase
        .from('message_threads')
        .select('*')
        .eq('archived', false)
        .order('last_message_at', { ascending: false })
        .limit(1);

      const [classesResult, balancesResult, threadsResult] = await Promise.all([
        classesQuery,
        balancesQuery,
        threadsQuery,
      ]);

      if (classesResult.error) throw classesResult.error;
      if (balancesResult.error) throw balancesResult.error;
      if (threadsResult.error) throw threadsResult.error;

      return {
        classes: uniqueClasses((classesResult.data ?? []) as { classes: Class | null }[]),
        balances: (balancesResult.data ?? []) as AccountBalance[],
        latestThread: ((threadsResult.data ?? []) as MessageThread[])[0] ?? null,
      };
    },
  });
}

function ParentToday() {
  const { data, isLoading } = useParentToday();
  const outstandingCents =
    data?.balances.reduce((total, balance) => total + balance.outstanding_cents, 0) ?? 0;
  const openBalanceCount =
    data?.balances.reduce((total, balance) => total + balance.open_count, 0) ?? 0;

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Welcome
        </p>
        <h1 className="mt-2">Today</h1>
      </header>

      <section aria-labelledby="upcoming-heading" className="surface-elevated p-6">
        <h2 id="upcoming-heading" className="text-[var(--text-xl)]">
          Upcoming classes
        </h2>
        {isLoading && (
          <p className="mt-3" aria-live="polite">
            Loading…
          </p>
        )}
        {data?.classes.length === 0 && (
          <p className="mt-3 max-w-prose text-[var(--color-ink-muted)]">
            Once your dancer is enrolled, their schedule will appear here.
          </p>
        )}
        {data && data.classes.length > 0 && (
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {data.classes.slice(0, 4).map((c) => (
              <li
                key={c.id}
                className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-4"
              >
                <p className="font-medium">{c.name}</p>
                <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)] capitalize">
                  {classTime(c)} · {c.room}
                </p>
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/parent/schedule"
          className="mt-4 inline-flex font-medium text-[var(--color-accent)] text-[var(--text-sm)]"
        >
          View full schedule
        </Link>
      </section>

      <section aria-labelledby="notifications-heading" className="surface-elevated p-6">
        <h2 id="notifications-heading" className="text-[var(--text-xl)]">
          Recent
        </h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Link
            to="/parent/billing"
            className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-4 transition-colors hover:bg-[var(--color-surface-sunken)]"
          >
            <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Billing</p>
            <p className="mt-1 font-[var(--font-display)] text-[var(--text-2xl)]">
              {formatCents(outstandingCents)}
            </p>
            <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              {openBalanceCount} open {openBalanceCount === 1 ? 'charge' : 'charges'}
            </p>
          </Link>
          <Link
            to="/parent/messages"
            className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-4 transition-colors hover:bg-[var(--color-surface-sunken)]"
          >
            <div className="flex items-center justify-between gap-3">
              <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Messages</p>
              <Badge tone={data?.latestThread ? 'accent' : 'neutral'}>
                {data?.latestThread ? 'New' : 'Clear'}
              </Badge>
            </div>
            <p className="mt-2 font-medium">
              {data?.latestThread?.subject ?? 'No recent messages'}
            </p>
          </Link>
        </div>
      </section>
    </div>
  );
}
